<template>
  <div>
    <div>
      <v-alert type="info" icon="mdi-alert-circle-outline" class="my-4">
        {{ $t("message.consumption-accounting-info") }}
      </v-alert>
    </div>
    <v-form ref="form" class="form">
      <v-row>
        <v-col class="py-0" cols="12" sm="4">
          <v-autocomplete
            v-model="document.document_type"
            :items="types"
            :label="$t('consumption-accounting.type')"
            item-text="name"
            item-value="id"
            :rules="[rules.required, backEndValidation('document_type')]"
            :readonly="readonly"
            :error-messages="documentTypeError"
            @change="getTemplates"
          ></v-autocomplete>
        </v-col>
        <v-col class="py-0" cols="12" sm="4">
          <v-autocomplete
            v-model="document.billing_period"
            :items="billingPeriods"
            item-text="name"
            item-value="id"
            :label="$t('consumption-accounting.billing-period')"
            :rules="[rules.required, backEndValidation('billing-period')]"
            :readonly="readonly"
            :error-messages="billingPeriodError"
          ></v-autocomplete>
        </v-col>
        <v-col class="py-0" cols="12" sm="4">
          <v-select
            v-model="document.document_template"
            :items="templates"
            :label="$t('consumption-accounting.template')"
            item-text="name"
            item-value="id"
            :rules="[rules.required, backEndValidation('document_template')]"
            :readonly="readonly"
          ></v-select>
        </v-col>
        <v-col class="py-0" cols="12" sm="4">
          <v-autocomplete
            v-model="document.destination_folder"
            :items="folders"
            item-text="name"
            item-value="id"
            :label="$t('consumption-accounting.folder')"
            :rules="[rules.required, backEndValidation('destination_folder')]"
            :readonly="readonly"
          ></v-autocomplete>
        </v-col>
        <v-col class="py-0" cols="12" sm="4">
          <AutocompleteContacts
            :item="document.sender"
            label="consumption-accounting.sender"
            :updated="val => (document.sender = val)"
            :rules="[rules.required, backEndValidation('sender')]"
            :readonly="readonly"
          ></AutocompleteContacts>
        </v-col>
        <v-col class="py-0" cols="12" sm="4">
          <AutocompleteContacts
            :item="document.creditor"
            label="consumption-accounting.creditor"
            :updated="val => (document.creditor = val)"
            :rules="[rules.required, backEndValidation('creditor')]"
            :readonly="readonly"
          ></AutocompleteContacts>
        </v-col>
        <v-col class="py-0" cols="12" sm="4">
          <v-checkbox
            v-model="document.consumption_period_from_contract"
            color="primary"
            :label="
              $t('consumption-accounting.consumption-period-from-contracts')
            "
            class="checkbox"
            :readonly="readonly"
          ></v-checkbox>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-btn
            color="primary"
            class="mr-2 mb-2"
            :loading="loading"
            v-on:click="calculate"
            :disabled="tableLoading"
          >
            {{ $t("consumption-accounting.calculate") }}
          </v-btn>
          <v-btn
            color="primary"
            class="mr-2 mb-2"
            :loading="loading"
            v-on:click="generateDocument"
            :disabled="accessibility"
          >
            {{ $t("consumption-accounting.generate") }}
          </v-btn>
          <v-btn
            color="primary"
            class="mr-2 mb-2"
            :loading="loading"
            v-on:click="copyToClipboard"
            :disabled="accessibility"
          >
            {{ $t("consumption-accounting.copy-to-clipboard") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>

    <PreRenderConsumptionAccounting
      ref="table"
      :type="calculateType"
      :loading="tableLoading"
      :consumption-period-from-contract="
        !!document.consumption_period_from_contract
      "
      :billingPeriod="billingPeriod"
      @updateItems="val => (this.items = val)"
    ></PreRenderConsumptionAccounting>
  </div>
</template>

<script>
import { rules } from "../../../mixins/rules";
import { projectPermissions } from "../../../mixins/projects/projectPermissions";
import AutocompleteContacts from "../../../common/inputs/AutocompleteContacts";
import PreRenderConsumptionAccounting from "./PreRenderConsumptionAccounting";
export default {
  name: "",
  mixins: [rules, projectPermissions],
  components: {
    AutocompleteContacts,
    PreRenderConsumptionAccounting
  },
  data: () => ({
    document: {
      consumption_period_from_contract: true
    },
    tableLoading: false,
    loading: false,
    items: [],
    documentTypeError: "",
    billingPeriodError: "",
    calculateType: {}
  }),
  computed: {
    type() {
      return this.types.find(i => i.id === this.document.document_type) || {};
    },
    templates: {
      get() {
        return this.$store.getters["documentTemplateStore/getList"];
      }
    },
    types: {
      get() {
        return (this.$store.getters["documentTypeStore/getList"] || []).filter(
          i =>
            [
              "consumption-bill",
              "consumption-receipt",
              "proportions-bill"
            ].find(u => u === i.id)
        );
      }
    },
    billingPeriods: {
      get() {
        return this.$store.getters[
          "projectAccountingStore/billingPeriodStore/getList"
        ];
      }
    },
    folders: {
      get() {
        return this.$store.getters["folderStore/getList"];
      }
    },
    billingPeriodCostCentrePivots: {
      get() {
        return this.$store.getters[
          "projectAccountingStore/billingPeriodCostCentreStore/getList"
        ];
      }
    },
    billingPeriod() {
      return (
        this.billingPeriods.find(i => i.id === this.document.billing_period) ||
        {}
      );
    },
    readonly: {
      get() {
        return !this.hasCurrentProjectPermissions("project.update");
      }
    },
    accessibility() {
      return this.items.length === 0;
    }
  },
  methods: {
    async formTable() {
      if (!this.document.billing_period || !this.document.document_type) {
        return false;
      }

      this.tableLoading = true;
      this.$store.commit(
        "projectAccountingStore/consumptionAccountingStore/resetList"
      );

      try {
        let { consumers } = await this.fetchCostCentres();

        let params = {
          billing_period: this.document.billing_period,
          project_id: this.$route.params.project_id,
          cost_centres: consumers.map(i => i._cost_centre.id).join(",")
        };
        if (!this.document.consumption_period_from_contract) {
          params.consumption_period = `${this.billingPeriod.start_date}/${this.billingPeriod.end_date}`;
        }
        let dispatch = "";
        if (this.type.id === "consumption-receipt") {
          dispatch =
            "projectAccountingStore/consumptionAccountingStore/getCountsList";
        }
        if (this.type.id === "proportions-bill") {
          dispatch =
            "projectAccountingStore/consumptionAccountingStore/getProportionsList";
        }
        if (this.type.id === "consumption-bill") {
          dispatch =
            "projectAccountingStore/consumptionAccountingStore/getConsumptionList";
        }
        dispatch &&
          (await this.$store.dispatch(dispatch, {
            params,
            errorHandler: this.errorHandler.bind(this, () => {
              this.$refs.form.validate();
            }),
            onSuccess: ({ list }) => {
              this.list = list;
            }
          }));
      } catch (e) {
        // nothing to do here
      } finally {
        this.tableLoading = false;
      }
    },
    async fetchCostCentres() {
      await this.$store.dispatch(
        "projectAccountingStore/billingPeriodCostCentreStore/getList",
        {
          params: {
            limit: -1,
            project_id: this.$route.params.project_id,
            billing_period_id: this.billingPeriod.id
          }
        }
      );

      let providers = this.billingPeriodCostCentrePivots.filter(
        i => i.provider
      );
      let consumers = this.billingPeriodCostCentrePivots.filter(
        i => i.consumer
      );

      if (providers.length === 0 || consumers.length === 0) {
        this.$store.dispatch(
          "notificationStore/createNotification",
          {
            text: this.$t("message.error-providers-or-consumers-not-set"),
            type: "error",
            value: true
          },
          { root: true }
        );
        throw new Error("providers or consumers empty");
      }

      return { providers, consumers };
    },
    getTemplates() {
      this.$store.dispatch("documentTemplateStore/getList", {
        params: {
          filters: {
            document_type: this.document.document_type
          }
        }
      });
    },
    generateDocument() {
      this.clearBackEndErrors();
      let currentFormValidate = this.$refs.form.validate();
      let tableFormValidate = this.$refs.table.validate();
      if (currentFormValidate && tableFormValidate) {
        this.loading = true;
        let params = {
          project_id: this.$route.params.project_id,
          items: this.items.map(i => ({
            ...this.document,
            ...i
          }))
        };
        this.$store
          .dispatch(
            "projectAccountingStore/consumptionAccountingStore/create",
            {
              params
            }
          )
          .finally(() => (this.loading = false));
      }
    },
    copyToClipboard() {
      this.$refs.table.copyToClipboard();
    },
    calculate() {
      if (!this.document.billing_period || !this.document.document_type) {
        this.billingPeriodError = this.$t(
          "consumption-accounting.billing_period_error"
        );
        this.documentTypeError = this.$t(
          "consumption-accounting.document_type_error"
        );
      } else {
        this.billingPeriodError = "";
        this.documentTypeError = "";
        this.calculateType = this.type;

        this.formTable();
      }
    }
  },

  mounted() {
    this.$store.commit(
      "projectAccountingStore/consumptionAccountingStore/resetList"
    );
    this.$store.dispatch("folderStore/getList", {
      params: { limit: -1 }
    });
    this.$store.dispatch("documentTypeStore/getList", {
      params: { limit: -1 }
    });
    this.$store.dispatch("projectAccountingStore/billingPeriodStore/getList", {
      params: {
        order: "name:desc",
        limit: -1,
        project_id: this.$route.params.project_id
      }
    });
  }
};
</script>

<style scoped></style>
